import { useMemo, useState } from "react";
import { metresToFeet, msToKnots, prettyCallsign } from "../lib/format.js";
import "./StatsBar.css";

const STORAGE_KEY = "stats-bar-open";

function Stat({ value, label, ariaLabel }) {
  return (
    <div className="stat" role="listitem" aria-label={ariaLabel}>
      <span className="stat-value" aria-hidden="true">
        {value}
      </span>
      <span className="stat-label" aria-hidden="true">
        {label}
      </span>
    </div>
  );
}

export default function StatsBar({ aircraft, collapsible = false }) {
  const [open, setOpen] = useState(() => {
    if (!collapsible) return true;
    try {
      return localStorage.getItem(STORAGE_KEY) !== "false";
    } catch {
      return true;
    }
  });

  const toggle = () => {
    setOpen((prev) => {
      const next = !prev;
      try {
        localStorage.setItem(STORAGE_KEY, String(next));
      } catch {
        /* ignore */
      }
      return next;
    });
  };

  const stats = useMemo(() => {
    const list = Array.isArray(aircraft) ? aircraft : [];
    const total = list.length;
    if (total === 0) return { total: 0 };

    let airborne = 0;
    let onGround = 0;
    let highest = null;
    let fastest = null;
    const countryCounts = new Map();

    for (const a of list) {
      if (a.onGround) onGround += 1;
      else airborne += 1;

      if (
        a.baroAltitude != null &&
        !a.onGround &&
        (highest == null || a.baroAltitude > highest.baroAltitude)
      ) {
        highest = a;
      }

      if (
        a.velocity != null &&
        (fastest == null || a.velocity > fastest.velocity)
      ) {
        fastest = a;
      }

      const country = a.originCountry || "Unknown";
      countryCounts.set(country, (countryCounts.get(country) || 0) + 1);
    }

    const topCountries = [...countryCounts.entries()]
      .sort((a, b) => b[1] - a[1])
      .slice(0, 3);

    return { total, airborne, onGround, highest, fastest, topCountries };
  }, [aircraft]);

  const { total, airborne, onGround, highest, fastest, topCountries } = stats;

  const highestFt = highest ? metresToFeet(highest.baroAltitude) : null;
  const highestValue =
    highestFt != null ? `${highestFt.toLocaleString()} ft` : "—";
  const highestCallsign = highest ? prettyCallsign(highest.callsign) : "—";

  const fastestKn = fastest ? msToKnots(fastest.velocity) : null;
  const fastestValue = fastestKn != null ? `${fastestKn} kn` : "—";
  const fastestCallsign = fastest ? prettyCallsign(fastest.callsign) : "—";

  const body =
    total === 0 ? (
      <section className="stats-bar stats-empty" aria-label="Aircraft statistics">
        <p className="stats-empty-text">No aircraft in view yet.</p>
      </section>
    ) : (
      <section
        className="stats-bar"
        role="list"
        aria-label="At a glance statistics for aircraft in view"
      >
        <Stat
          value={total.toLocaleString()}
          label="In view"
          ariaLabel={`${total} aircraft in view`}
        />
        <Stat
          value={airborne.toLocaleString()}
          label="Airborne"
          ariaLabel={`${airborne} airborne`}
        />
        <Stat
          value={onGround.toLocaleString()}
          label="On the ground"
          ariaLabel={`${onGround} on the ground`}
        />
        <Stat
          value={highestValue}
          label={`Highest · ${highestCallsign}`}
          ariaLabel={`Highest aircraft ${highestCallsign} at ${highestValue}`}
        />
        <Stat
          value={fastestValue}
          label={`Fastest · ${fastestCallsign}`}
          ariaLabel={`Fastest aircraft ${fastestCallsign} at ${fastestValue}`}
        />
        {topCountries.map(([country, count]) => (
          <Stat
            key={country}
            value={count.toLocaleString()}
            label={country}
            ariaLabel={`${count} from ${country}`}
          />
        ))}
      </section>
    );

  if (!collapsible) return body;

  return (
    <div className="stats-collapsible">
      <button
        type="button"
        className="stats-toggle"
        onClick={toggle}
        aria-expanded={open}
      >
        <span className={`stats-toggle-chevron${open ? " is-open" : ""}`} aria-hidden="true">
          ▸
        </span>
        <span className="stats-toggle-label">Stats</span>
        {total > 0 && (
          <span className="stats-toggle-summary">{total.toLocaleString()} in view</span>
        )}
      </button>
      {open && body}
    </div>
  );
}
